import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Menu, 
  Bell, 
  User, 
  Settings,
  LogOut,
  ChevronDown
} from 'lucide-react';
import { getUserInfo, UserInfo as AuthUserInfo, clearAllStoredData } from '../../utils/auth';

// Types
interface UserInfo {
  accountId: string;
  userName: string;
  userId: string;
  role: string;
}

interface TopbarProps {
  title: string;
  subtitle?: string;
  isMobileSidebarOpen: boolean;
  setIsMobileSidebarOpen: (open: boolean) => void;
  isProfileDropdownOpen: boolean;
  setIsProfileDropdownOpen: (open: boolean) => void;
  showMobileLogo?: boolean;
  mobileLogoIcon?: React.ReactNode;
  mobileLogoBg?: string;
  projectDetail?: {
    logo?: string;
    name: string;
  };
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  subtitle,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
  isProfileDropdownOpen,
  setIsProfileDropdownOpen,
  showMobileLogo = false,
  mobileLogoIcon,
  mobileLogoBg = "bg-gradient-to-br from-blue-500 to-purple-600",
  projectDetail
}) => {
  const navigate = useNavigate();
  
  // Get actual user info from auth
  const authUserInfo = getUserInfo();
  
  // 로그아웃 처리
  const handleLogout = () => {
    // 모든 저장된 정보 삭제
    clearAllStoredData();
    
    // 프로필 드롭다운 닫기
    setIsProfileDropdownOpen(false);
    
    // 로그인 화면으로 이동
    navigate('/auth/login');
  };
  
  // Format account code to 2-4-4 format
  const formatAccountCode = (code: string): string => {
    if (!code || code === 'Unknown') return 'Unknown';
    const cleaned = code.replace(/[^A-Z0-9]/g, ''); // Remove non-alphanumeric characters
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}-${cleaned.slice(6, 10)}`;
    }
    return code; // Return original if not long enough
  };
  
  const userInfo: UserInfo = authUserInfo ? {
    accountId: formatAccountCode(authUserInfo.accountCode || 'Unknown'),
    userName: authUserInfo.userId || 'Unknown',
    userId: authUserInfo.loginId || 'Unknown',
    role: authUserInfo.userType === 'root' ? '루트 관리자' : 'IAM 관리자'
  } : {
    accountId: 'Unknown',
    userName: 'Unknown',
    userId: 'Unknown',
    role: 'Unknown'
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 px-4 sm:px-6 flex items-center justify-between">
      <div className="flex items-center">
        {/* Mobile menu button */}
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden mr-2"
        >
          <Menu className="w-5 h-5" />
        </button>
        
        {/* Mobile Logo (for project/shop pages) */}
        {showMobileLogo && (
          <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center ml-2 mr-3 lg:hidden overflow-hidden">
            {projectDetail?.logo ? (
              <img 
                src={projectDetail.logo} 
                alt={projectDetail.name}
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`text-xl ${projectDetail?.logo ? 'hidden' : ''}`}>📁</div>
          </div>
        )}
        
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">{title}</h1>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Notifications */}
        <button className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <Bell className="w-5 h-5 text-gray-600" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>

        {/* User Profile */}
        <div className="relative">
          <button
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            className="flex items-center space-x-2 sm:space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{userInfo.userName}</p>
              <p className="text-xs text-gray-500">{userInfo.role}</p>
            </div>
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600" />
            </div>
            <ChevronDown className="w-4 h-4 text-gray-600 hidden sm:block" />
          </button>

          {/* Dropdown Menu */}
          {isProfileDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10">
              <div className="px-4 py-2 border-b border-gray-200">
                <p className="text-sm font-medium text-gray-900">{userInfo.userId}</p>
                <p className="text-xs text-gray-500">{userInfo.accountId}</p>
              </div>
              <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <Settings className="w-4 h-4 mr-2" />
                계정 설정
              </button>
              <button 
                onClick={handleLogout}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              >
                <LogOut className="w-4 h-4 mr-2" />
                로그아웃
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;